/**
 * OnboardingMedications.jsx — v213.7 (C-A-OB-FIX)
 *
 * Screen 6 of 7. Same pattern as Allergies. Type-to-search picker uses
 * COMMON_MEDICATIONS from medicalLists.js (same source as Profile editor).
 *
 * v213.7 changes:
 *  - Type-to-search picker (was freeform text input).
 *  - "Not taking any medications" upgraded to SecondaryButton.
 *  - defaultPurpose pre-fills the For: field when picking a known med.
 */
import { useState, useMemo } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { Pill, Search, X } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useOnboardingStore } from '../../stores'
import { COMMON_MEDICATIONS, MED_DOSE_UNITS, MED_FREQUENCIES, formatDose } from '../../data/medicalLists'
import WheelPicker from '../../components/WheelPicker'
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  OrellaBubble,
  FilledBanner,
  PrimaryButton,
  SecondaryButton,
  Footer,
  ScrollBody,
  ConfirmCard,
  AddAnotherButton,
  EmptyState,
  TextField,
  useScrollToTop,
  NAVY,
  BLUE,
  ICE_BLUE,
  TEXT_MUTED,
  TEXT_HINT,
  BORDER_LIGHT,
  LIGHT_BLUE,
  PROFILE_STEPS,
} from './onboardingPrimitives'

/**
 * AddMedicationSheet — type-to-search picker over COMMON_MEDICATIONS,
 * then optional dose / frequency / purpose details.
 */
function AddMedicationSheet({ onSave, onClose }) {
  const { t } = useI18n()
  const [step, setStep] = useState('search') // 'search' | 'details'
  const [pick, setPick] = useState(null)
  const [query, setQuery] = useState('')
  const [doseAmount, setDoseAmount] = useState('')
  const [doseUnit, setDoseUnit] = useState('mg')
  const [frequency, setFrequency] = useState('')
  const [purpose, setPurpose] = useState('')

  const FREQ_OPTS = useMemo(() => MED_FREQUENCIES.map(f => ({
    value: f.value, label: t('profileMedications.' + f.key),
  })), [t])

  const results = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return COMMON_MEDICATIONS.filter((x) => x.common).slice(0, 30)
    return COMMON_MEDICATIONS.filter((x) => x.name.toLowerCase().includes(term)).slice(0, 40)
  }, [query])

  const handleSelect = (item) => {
    setPick(item)
    setPurpose(item.defaultPurpose || '')
    setStep('details')
  }

  const handleAddCustom = () => {
    if (!query.trim()) return
    setPick({ id: `custom-${Date.now()}`, name: query.trim(), custom: true })
    setStep('details')
  }

  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(21,53,79,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full bg-white"
        style={{
          maxWidth: 460, maxHeight: '88vh',
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          padding: '20px 18px 22px', overflowY: 'auto',
          paddingBottom: 'max(22px, env(safe-area-inset-bottom, 0px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 500, color: NAVY }}>
            {step === 'search'  && t('onboarding.meds.search', 'Find your medication')}
            {step === 'details' && (pick?.name || '')}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full flex items-center justify-center"
            style={{ width: 32, height: 32, background: '#F2F4F7', border: 'none', cursor: 'pointer' }}
            aria-label="Close"
          >
            <X size={16} color={NAVY} />
          </button>
        </div>

        {step === 'search' && (
          <>
            <div className="flex items-center gap-2" style={{
              padding: '10px 12px', borderRadius: 11,
              border: `1.5px solid ${BORDER_LIGHT}`, marginBottom: 12,
            }}>
              <Search size={18} color={TEXT_HINT} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('onboarding.meds.searchPh', 'Type a medication name...')}
                className="flex-1 focus:outline-none"
                style={{ fontSize: 15, color: NAVY, border: 'none', background: 'transparent' }}
              />
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {results.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className="w-full text-left transition"
                  style={{
                    padding: '12px 12px',
                    fontSize: 14.5,
                    color: NAVY,
                    background: '#fff',
                    border: 'none',
                    borderBottom: `1px solid ${BORDER_LIGHT}`,
                    cursor: 'pointer',
                  }}
                >
                  {item.name}
                  {item.category && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: TEXT_HINT }}>{item.category}</span>
                  )}
                </button>
              ))}
              {results.length === 0 && query.trim() && (
                <button
                  onClick={handleAddCustom}
                  className="w-full text-left transition"
                  style={{
                    padding: '14px 12px', fontSize: 14.5,
                    color: BLUE, fontWeight: 500,
                    background: ICE_BLUE,
                    border: `1.5px dashed ${LIGHT_BLUE}`,
                    borderRadius: 12, cursor: 'pointer', marginTop: 4,
                  }}
                >
                  + {t('onboarding.meds.addCustom', { defaultValue: `Add "${query.trim()}"`, name: query.trim() })}
                </button>
              )}
            </div>
          </>
        )}

        {step === 'details' && pick && (
          <>
            <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 14 }}>
              {t('onboarding.meds.detailsHint', 'A bit more detail (optional but helpful for doctors).')}
            </p>
            <div style={{ marginBottom: 12 }}>
              <label className="block font-medium mb-1.5" style={{ fontSize: 13, color: NAVY }}>
                {t('onboarding.meds.dose', 'Dose')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={doseAmount}
                  onChange={(e) => setDoseAmount(e.target.value)}
                  placeholder="10"
                  className="focus:outline-none transition"
                  style={{ width: 96, height: 46, padding: '0 12px', borderRadius: 11, border: `1.5px solid ${BORDER_LIGHT}`, background: '#fff', color: NAVY, fontSize: 15 }}
                  onFocus={(e) => (e.target.style.borderColor = BLUE)}
                  onBlur={(e) => (e.target.style.borderColor = BORDER_LIGHT)}
                />
                <div className="flex-1">
                  <WheelPicker
                    value={doseUnit}
                    options={MED_DOSE_UNITS}
                    onChange={setDoseUnit}
                    title={t('profileMedications.doseUnit', { defaultValue: 'Unit' })}
                    doneLabel={t('common.done', { defaultValue: 'Done' })}
                    fieldStyle={{ height: 46, padding: '0 12px', borderRadius: 11, border: `1.5px solid ${BORDER_LIGHT}`, background: '#fff' }}
                    valueColor={NAVY}
                  />
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="block font-medium mb-1.5" style={{ fontSize: 13, color: NAVY }}>
                {t('onboarding.meds.frequency', 'Frequency')}
              </label>
              <WheelPicker
                value={frequency}
                options={FREQ_OPTS}
                onChange={setFrequency}
                title={t('onboarding.meds.frequency', 'Frequency')}
                placeholder={t('onboarding.meds.freqPh', 'once daily')}
                doneLabel={t('common.done', { defaultValue: 'Done' })}
                fieldStyle={{ height: 46, padding: '0 12px', borderRadius: 11, border: `1.5px solid ${BORDER_LIGHT}`, background: '#fff' }}
                valueColor={NAVY}
              />
            </div>
            <TextField
              label={t('onboarding.meds.purpose', 'For (optional)')}
              value={purpose}
              onChange={setPurpose}
              placeholder={t('onboarding.meds.purposePh', 'e.g. blood pressure, cholesterol')}
              required={false}
            />
            <div style={{ marginTop: 8 }}>
              <PrimaryButton
                data-bar-exempt="saves the item inside its add sheet; the step's own Continue is in the Footer bar"
                onClick={() => {
                  onSave({
                    id: pick.id,
                    name: pick.name,
                    dosage: formatDose(doseAmount, doseUnit),
                    frequency: frequency.trim(),
                    purpose: purpose.trim(),
                    custom: !!pick.custom,
                    source: 'manual',
                    confirmed: true,
                  })
                  onClose()
                }}
              >
                {t('common.add', 'Add medication')}
              </PrimaryButton>
              <SecondaryButton onClick={() => setStep('search')}>
                {t('onboarding.meds.back', '← Back to search')}
              </SecondaryButton>
            </div>
          </>
        )}
      </div>
    </div>
    </OverlayPortal>
  )
}

export default function OnboardingMedications({ onNext, onBack }) {
  const { t } = useI18n()
  const medications = useOnboardingStore((s) => s.medications)
  const setMedications = useOnboardingStore((s) => s.setMedications)
  const autofillResult = useOnboardingStore((s) => s.autofillResult)

  const [sheetOpen, setSheetOpen] = useState(false)

  useScrollToTop('medications')

  const items = medications.items
  const hasAutofill = !!autofillResult

  const toggleConfirm = (id) => {
    setMedications({
      items: items.map((it) => (it.id === id ? { ...it, confirmed: !it.confirmed } : it)),
      none: false,
    })
  }
  // review r1 (M2): only this per-item removal is remembered against a later upload;
  // "No medications" below clears the list without suppressing anything.
  const removeItem = (id) => {
    setMedications({ items: items.filter((it) => it.id !== id), none: medications.none }, { perItem: true })
  }
  const addItem = (item) => {
    setMedications({ items: [...items, item], none: false })
  }
  const declareNone = () => {
    setMedications({ items: [], none: true })
    onNext()
  }

  const canContinue = items.length > 0 || medications.none

  return (
    <>
      <OnboardingShell>
        <ScreenHeader onBack={onBack} step={3} total={PROFILE_STEPS} />
        <ScreenIntro
          eyebrow={t('onboarding.meds.eyebrow', 'Medications')}
          title={hasAutofill
            ? t('onboarding.meds.titleAi', 'Confirm your medications')
            : t('onboarding.meds.titleManual', 'Anything you take?')}
          dialogue={hasAutofill
            ? t('onboarding.meds.dialogueAi', 'These came from your record. Confirm, fix a dose, or remove what you no longer take.')
            : t('onboarding.meds.dialogueManual', 'Prescriptions, supplements, OTC — all count.')}
        />

        <ScrollBody>
          {hasAutofill && items.length > 0 && (
            <FilledBanner>{t('onboarding.meds.filled', 'Filled by Orella from your record — review & confirm')}</FilledBanner>
          )}
          {items.length === 0 ? (
            <EmptyState
              icon={Pill}
              title={t('onboarding.meds.empty', 'No medications added yet')}
              ctaLabel={t('onboarding.meds.addCta', 'Add medication')}
              onAdd={() => setSheetOpen(true)}
            />
          ) : (
            <>
              {items.map((item) => (
                <ConfirmCard
                  key={item.id}
                  confirmed={item.confirmed}
                  onToggle={() => toggleConfirm(item.id)}
                  onRemove={() => removeItem(item.id)}
                >
                  <div className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 14.5, fontWeight: 500, color: NAVY }}>{item.name}</span>
                    {item.source === 'AI' && (
                      <span style={{ fontSize: 10.5, fontWeight: 500, padding: '2px 6px', borderRadius: 4, background: ICE_BLUE, color: BLUE }}>AI</span>
                    )}
                  </div>
                  {(item.dosage || item.frequency) && (
                    <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 3 }}>
                      {[item.dosage, item.frequency].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {item.purpose && (
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: BLUE }}>
                      {t('onboarding.meds.forLabel', 'For:')} {item.purpose}
                    </div>
                  )}
                </ConfirmCard>
              ))}
              <AddAnotherButton
                label={t('common.addAnother', 'Add another')}
                onClick={() => setSheetOpen(true)}
              />
            </>
          )}
        </ScrollBody>

        <Footer>
          <PrimaryButton onClick={onNext} disabled={!canContinue}>
            {t('common.continue', 'Continue')}
          </PrimaryButton>
          <SecondaryButton onClick={declareNone}>
            {t('onboarding.meds.none', 'Not taking any medications')}
          </SecondaryButton>
        </Footer>
      </OnboardingShell>

      {sheetOpen && <AddMedicationSheet onSave={addItem} onClose={() => setSheetOpen(false)} />}
    </>
  )
}
